import React, { useState, useEffect } from "react";
import ProductCard from "../../components/common/ProductCardComponent/Index";
import Header from "../../assests/rt (1).png";
import FilterSidebar from "../../components/FilterSidebar/index";
import ReactPaginate from "react-paginate";
import { fetch_Products } from "../../Services/ProductServices";
import Spinner from "../../components/common/loadingSpinnercomponent/Spinner";
import "./ProductsList.css";

const ProductList = () => {
  const [allProducts, setAllProducts] = useState([]); // All fetched products
  const [filteredProducts, setFilteredProducts] = useState([]); // Products after applying filters and sorting
  const [currentPage, setCurrentPage] = useState(0); // Current page for pagination
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const itemsPerPage = 8; // Number of items per page

  // Define the state for filters
  const [filters, setFilters] = useState({
    category: null,
    size: null,
    availability: null,
    minPrice: null,
    maxPrice: null,
  });

  // State for sorting
  const [sortOption, setSortOption] = useState("latest"); // Default sort option

  /**
   * Initialize filters and sort option from the URL query string when the component mounts.
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialFilters = {
      category: urlParams.get("category") || null,
      size: urlParams.get("size") || null,
      availability: urlParams.get("availability") || null,
      minPrice: urlParams.get("min_price")
        ? parseInt(urlParams.get("min_price"), 10)
        : null,
      maxPrice: urlParams.get("max_price")
        ? parseInt(urlParams.get("max_price"), 10)
        : null,
    };
    const initialSort = urlParams.get("sort") || "latest";
    setFilters(initialFilters);
    setSortOption(initialSort);
  }, []);

  /**
   * Handle filter changes received from the FilterSidebar component.
   * @param {Object} newFilters - The updated filter values.
   */
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    console.log("Updated Filters:", newFilters);
  };

  /**
   * Fetch products based on the current filters.
   */
  const fetchDataWithFilters = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Construct the query string based on current filters
      const queryString = buildQueryParams(filters);

      // Update the URL with the current query string and sort option for persistence
      updateUrlWithQueryString(queryString, sortOption);

      // Fetch products using the constructed query string
      const productsData = await fetch_Products(queryString);
      setAllProducts(productsData || []);
      setCurrentPage(0); // Reset to first page when filters change
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Failed to fetch products. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Build query parameters string based on the filters.
   * Excludes the sort option as sorting is handled client-side.
   * @param {Object} filters - The current filter states.
   * @returns {string} - The constructed query string.
   */
  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();

    // Always include all filter params, using empty strings if not set
    params.set("category", filters.category || "");
    params.set("size", filters.size || "");
    params.set("availability", filters.availability || "");
    params.set("min_price", filters.minPrice !== null ? filters.minPrice : "");
    params.set("max_price", filters.maxPrice !== null ? filters.maxPrice : "");

    return params.toString();
  };

  /**
   * Update the browser's URL with the current query string and sort option without reloading the page.
   * @param {string} queryString - The query string to append to the URL.
   * @param {string} sortOption - The current sort option.
   */
  const updateUrlWithQueryString = (queryString, sortOption) => {
    const params = new URLSearchParams(queryString);
    params.set("sort", sortOption);
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: url }, "", url);
  };

  /**
   * Fetch products whenever filters are updated.
   */
  useEffect(() => {
    fetchDataWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /**
   * Helper function to determine the effective display price of a product.
   * Uses OfferPrice if available; otherwise, uses Price.
   * @param {Object} product - The product object.
   * @returns {number} - The effective display price.
   */
  const getDisplayPrice = (product) => {
    return product.OfferPrice > 0 ? product.OfferPrice : product.Price;
  };

  /**
   * Handle sorting whenever sortOption or allProducts change.
   * Sorts the products client-side based on the selected sort option.
   */
  useEffect(() => {
    if (!isLoading && !error) {
      let sortedProducts = [...allProducts];

      switch (sortOption) {
        case "price_low":
          sortedProducts.sort(
            (a, b) => getDisplayPrice(a) - getDisplayPrice(b)
          );
          break;
        case "price_high":
          sortedProducts.sort(
            (a, b) => getDisplayPrice(b) - getDisplayPrice(a)
          );
          break;
        case "latest":
        default:
          sortedProducts.sort(
            (a, b) => new Date(b.DateCreated) - new Date(a.DateCreated)
          );
          break;
      }

      setFilteredProducts(sortedProducts);
      setCurrentPage(0); // Reset to first page when sort changes
    }
  }, [sortOption, allProducts, isLoading, error]);

  /**
   * Handle browser navigation (back/forward) to synchronize filters and sort with URL.
   */
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const updatedFilters = {
        category: urlParams.get("category") || null,
        size: urlParams.get("size") || null,
        availability: urlParams.get("availability") || null,
        minPrice: urlParams.get("min_price")
          ? parseInt(urlParams.get("min_price"), 10)
          : null,
        maxPrice: urlParams.get("max_price")
          ? parseInt(urlParams.get("max_price"), 10)
          : null,
      };
      const updatedSort = urlParams.get("sort") || "latest";
      setFilters(updatedFilters);
      setSortOption(updatedSort);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  /**
   * Handle page change in pagination.
   * @param {Object} selectedItem - The selected page object.
   */
  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Retrieve the products for the current page.
   * @returns {Array} - The products to display on the current page.
   */
  const getPagedData = () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      return [];
    }
    const offset = currentPage * itemsPerPage;
    return filteredProducts.slice(offset, offset + itemsPerPage);
  };

  const currentItems = getPagedData();
  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min(
    (currentPage + 1) * itemsPerPage,
    filteredProducts.length || 0
  );

  return (
    <div>
      {isLoading && (
        <div className="loading-container">
          <Spinner />
        </div>
      )}
      {error && <div className="error-container">Error: {error}</div>}
      {!isLoading && !error && (
        <>
          <header className="header-image-container">
            <img src={Header} alt="Header" className="img-fluid" />
          </header>
          <div className="row mt-4 mx-0">
            <h1 className="h4 heading">All Products</h1>
            <div className="col text-right">
              <span className="items-count">
                Showing {endItem} of {filteredProducts?.length || 0} results
              </span>
              <div className="product-dropdown-container">
                <select
                  className="custom-dropdown"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="latest">Sort By Latest</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
                <i className="fa-solid fa-chevron-down dropdown-icon"></i>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-3">
                <FilterSidebar
                  onFilterChange={handleFilterChange}
                  initialFilters={filters}
                />
              </div>
              <div className="col-lg-9">
                {currentItems.length === 0 ? (
                  <div className="no-products">
                    No products found. Please adjust your filters.
                  </div>
                ) : (
                  <div className="product-list-grid">
                    {currentItems.map((product) => (
                      <ProductCard key={product.ProductId} product={product} />
                    ))}
                  </div>
                )}
                {filteredProducts.length > 0 && (
                  <div className="pagination-container">
                    <ReactPaginate
                      previousLabel={
                        <i className="fa-solid fa-chevron-left"></i>
                      }
                      nextLabel={<i className="fa-solid fa-chevron-right"></i>}
                      breakLabel="..."
                      pageCount={Math.ceil(
                        filteredProducts.length / itemsPerPage
                      )}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={handlePageClick}
                      containerClassName="pagination"
                      activeClassName="active"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductList;